import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  MdTrendingUp, MdCalendarMonth, MdGroups, MdAccountBalanceWallet,
  MdPeople, MdKingBed, MdBadge, MdPersonAddAlt1, MdWarning,
  MdLocalHospital, MdMedication, MdScience,
  MdReceiptLong, MdRefresh, MdBed,
} from 'react-icons/md';
import { PageShell, DataTable, StatCard, Badge } from '../../components/Ui';
import { navIcon } from '../../config/navIcons';
import { useAuth } from '../../auth/AuthContext';
import { ipApi } from '../../services/api';
import { useLiveData } from '../../hooks/useLiveData';
import GetStartedChecklist from '../../components/onboarding/GetStartedChecklist';
import { useHospitalDirectory } from '../../hooks/useHospitalDirectory';
import { mergeEmrSettings } from '../../config/emrSettings';
import { STAFF_ROLES as HOSPITAL_STAFF_ROLES } from '../../config/plans';
import { useHmsRecords } from '../../hooks/useHmsRecords';
import { buildKQIs } from '../../utils/qualityKpis';

function formatInr(n) { return `₹${Number(n || 0).toLocaleString('en-IN')}`; }

const QUICK_LINKS = [
  { path: '/manager/admin', label: 'Staff & Logins', desc: 'Add doctors, nurses, staff', accent: '#8b5cf6' },
  { path: '/reception/queue', label: 'OP Queue', desc: 'Patient check-in & tokens', accent: '#4f8ef7' },
  { path: '/reception/patients', label: 'Patients', desc: 'Register & search UHID', accent: '#06b6d4' },
  { path: '/hms/appointments', label: 'Appointments', desc: 'Book & manage slots', accent: '#00d4aa' },
  { path: '/doctor/queue', label: 'Doctor OPD', desc: 'Consultation & prescriptions', accent: '#10b981' },
  { path: '/hms/emr', label: 'EMR', desc: 'Electronic medical records', accent: '#0891b2' },
  { path: '/billing/receipts', label: 'Billing', desc: 'Receipts & payments', accent: '#f59e0b' },
  { path: '/ipd/list', label: 'IPD', desc: 'Admissions & discharges', accent: '#f97316' },
  { path: '/ipd/beds', label: 'Bed Master', desc: 'Ward & bed status', accent: '#ef4444' },
  { path: '/hms/discharge', label: 'Discharge Summary', desc: 'NABH discharge forms & print', accent: '#0d6e6a' },
  { path: '/hms/ot', label: 'OT Management', desc: 'Surgery scheduling & notes', accent: '#7c3aed' },
  { path: '/nurse/station', label: 'Nursing', desc: 'Vitals & patient care', accent: '#ec4899' },
  { path: '/pharmacy/desk', label: 'Pharmacy', desc: 'Medicines & inventory', accent: '#10b981' },
  { path: '/hms/store', label: 'Central Store', desc: 'Inventory, GRN & Purchase Orders', accent: '#16a34a' },
  { path: '/lab/orders', label: 'Laboratory', desc: 'Tests, results & reports', accent: '#06b6d4' },
  { path: '/hms/bloodbank', label: 'Blood Bank', desc: 'Donor, cross-match & issue', accent: '#dc2626' },
  { path: '/hms/diet', label: 'Diet / Nutrition', desc: 'Diet orders & menu planning', accent: '#84cc16' },
  { path: '/hms/analysis', label: 'Reports & Analytics', desc: 'Revenue, NABH KPIs & PDF', accent: '#6366f1' },
  { path: '/hr/dashboard', label: 'HR', desc: 'Staff & attendance', accent: '#6366f1' },
  { path: '/hms/maintenance', label: 'Maintenance', desc: 'Equipment & AMC tracking', accent: '#94a3b8' },
  { path: '/hms/feedback', label: 'Patient Feedback', desc: 'Ratings & complaints', accent: '#f59e0b' },
  { path: '/hms/sms', label: 'SMS / WhatsApp', desc: 'Bulk notifications', accent: '#64748b' },
  { path: '/hms/clinic-growth', label: 'Clinic Growth', desc: 'QR, reviews & staff updates', accent: '#0d6e6a' },
  { path: '/hms/settings', label: 'Settings', desc: 'Hospital profile & config', accent: '#475569' },
  { path: '/hms/icu', label: 'ICU / Critical Care', desc: 'Ventilator, APACHE II, vitals, drips', accent: '#dc2626' },
  { path: '/hms/radiology', label: 'Radiology / RIS', desc: 'Worklist, reports, PACS viewer', accent: '#8b5cf6' },
  { path: '/hms/infection', label: 'Infection Control', desc: 'HAI, CSSD, antibiotic stewardship', accent: '#f97316' },
  { path: '/hms/quality', label: 'Quality / NABH Audit', desc: 'Incidents, CAPA, KQI dashboard', accent: '#06b6d4' },
  { path: '/hms/telemedicine', label: 'Telemedicine', desc: 'Video consult & e-prescription', accent: '#10b981' },
  { path: '/hms/abha', label: 'ABHA / ABDM', desc: 'Health ID, consent, record sharing', accent: '#0891b2' },
  { path: '/hms/ai', label: 'AI Assist', desc: 'Status, enable/disable, AI setup', accent: '#4f46e5' },
  { path: '/hms/tpa', label: 'TPA / Insurance Claims', desc: 'Pre-auth, cashless, reimbursement', accent: '#7c3aed' },
  { path: '/hms/bmw', label: 'Bio-Medical Waste', desc: 'Yellow/Red/Blue bins, CPCB manifest', accent: '#16a34a' },
  { path: '/hms/payroll', label: 'Payroll & Accounts', desc: 'Salary, PF/ESI/TDS, P&L', accent: '#0369a1' },
  { path: '/hms/ambulance', label: 'Ambulance / Emergency', desc: 'Fleet, dispatch, ALS/BLS', accent: '#dc2626' },
  { path: '/hms/physio', label: 'Physiotherapy', desc: 'Session log, VAS, exercise plans', accent: '#059669' },
  { path: '/hms/dialysis', label: 'Dialysis Unit', desc: 'HD params, machine status, Kt/V', accent: '#2563eb' },
  { path: '/hms/documents', label: 'Document Management', desc: 'Policies, SOPs, consent forms, NABH', accent: '#9333ea' },
  { path: '/hms/mortuary', label: 'Mortuary', desc: 'Death register, MLC, body release, certs', accent: '#475569' },
  { path: '/hms/visitors', label: 'Visitor Management', desc: 'Digital pass, ICU slots, entry/exit log', accent: '#0284c7' },
  { path: '/hms/roster', label: 'Duty Roster', desc: 'M/E/N shifts, monthly schedule, all depts', accent: '#7c3aed' },
  { path: '/hms/nicu', label: 'NICU / Neonatal', desc: 'Apgar score, incubator, weight chart', accent: '#db2777' },
  { path: '/hms/housekeeping', label: 'Housekeeping & Laundry', desc: 'Cleaning board, linen, terminal disinfection', accent: '#0891b2' },
];

/* ── Mini bar chart (CSS only, no library) ── */
function MiniBarChart({ data, label, color = '#0d6e6a' }) {
  if (!data || !data.length) return <div className="mb-chart-empty">No data yet</div>;
  const max = Math.max(...data.map((d) => d.value), 1);
  return (
    <div className="mb-chart">
      {data.map((d, i) => (
        <div key={i} className="mb-bar-col" title={`${d.label}: ${formatInr(d.value)}`}>
          <div className="mb-bar-wrap">
            <div
              className="mb-bar"
              style={{ height: `${Math.max(4, (d.value / max) * 100)}%`, background: color }}
            />
          </div>
          <div className="mb-bar-label">{d.label}</div>
        </div>
      ))}
    </div>
  );
}

/* ── Bed occupancy visual ── */
function BedOccupancyBar({ occupied = 0, total = 0, available = 0, reserved = 0 }) {
  const pct = total > 0 ? Math.round((occupied / total) * 100) : 0;
  const tone = pct >= 90 ? '#ef4444' : pct >= 70 ? '#f97316' : '#10b981';
  return (
    <div className="bed-occ-wrap">
      <div className="bed-occ-bar-bg">
        <div className="bed-occ-bar-fill" style={{ width: `${pct}%`, background: tone }} />
      </div>
      <div className="bed-occ-legend">
        <span style={{ color: tone }}><strong>{pct}%</strong> occupancy</span>
        <span className="hint">{occupied} occupied · {available || (total - occupied)} available · {reserved || 0} reserved</span>
      </div>
    </div>
  );
}

/* ── Alert card ── */
function AlertCard({ icon: Icon, title, desc, color, to }) {
  const el = (
    <div className="dash-alert-card" style={{ borderLeft: `4px solid ${color}` }}>
      <Icon style={{ color, fontSize: 20, flexShrink: 0 }} />
      <div>
        <div className="dash-alert-title">{title}</div>
        {desc && <div className="dash-alert-desc">{desc}</div>}
      </div>
      {to && <span className="dash-alert-link">View →</span>}
    </div>
  );
  return to ? <Link to={to} style={{ textDecoration: 'none' }}>{el}</Link> : el;
}

function NabhKpiSection({ beds }) {
  const { records: incidents } = useHmsRecords('quality-incident', 200);
  const { records: feedbacks } = useHmsRecords('feedback', 200);
  const { records: neonates } = useHmsRecords('nicu-neonate', 200);
  const { records: handHygieneObs } = useHmsRecords('infection-handhygiene', 200);
  const { data: admData, loading: admLoading } = useLiveData(() => ipApi.getAdmissions(200), []);
  const { data: allAdmData, loading: allAdmLoading } = useLiveData(() => ipApi.getAdmissions(300, { includeDischarged: true }), []);
  const { data: labData, loading: labLoading } = useLiveData(() => ipApi.getLabOrders(200), []);

  const kpis = useMemo(() => buildKQIs({
    bedOccupancy: beds,
    admissions: admData?.admissions || [],
    allAdmissions: allAdmData?.admissions || [],
    incidents,
    feedbacks,
    labOrders: labData?.orders || [],
    neonates,
    handHygieneObs,
  }), [beds, admData, allAdmData, incidents, feedbacks, labData, neonates, handHygieneObs]);

  const loading = admLoading || allAdmLoading || labLoading;
  const liveCount = kpis.filter((k) => k.live).length;

  return (
    <div className="card mt" style={{ borderTop: '3px solid #6366f1' }}>
      <div className="card-head-row">
        <h3>🏆 NABH Quality Indicators</h3>
        <Link to="/hms/quality" className="btn btn-sm btn-secondary">Full quality module →</Link>
      </div>
      <p className="hint" style={{ marginTop: -4, marginBottom: 14 }}>
        {loading ? 'Loading live KPIs…' : `${liveCount} of ${kpis.length} indicators from live hospital data`}
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
        {kpis.map(({ indicator, value, target, status, icon, dept }) => (
          <div key={indicator} style={{ padding: '12px 14px', border: `1px solid ${status === 'ok' ? '#dcfce7' : status === 'warn' ? '#fed7aa' : status === 'bad' ? '#fca5a5' : '#e2e8f0'}`, borderLeft: `4px solid ${status === 'ok' ? '#10b981' : status === 'warn' ? '#f97316' : status === 'bad' ? '#ef4444' : '#94a3b8'}`, borderRadius: 10, background: status === 'ok' ? '#f0fdf4' : status === 'warn' ? '#fff7ed' : status === 'bad' ? '#fef2f2' : '#f8fafc' }}>
            <div style={{ fontSize: 18, marginBottom: 4 }}>{icon}</div>
            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>{indicator}</div>
            <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: 20, fontWeight: 900, color: status === 'ok' ? '#16a34a' : status === 'warn' ? '#ea580c' : status === 'bad' ? '#dc2626' : '#64748b' }}>{value}</div>
            <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 2 }}>Target: {target} · {dept}</div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 14, padding: '10px 14px', background: '#f8fafc', borderRadius: 8, fontSize: 12, color: '#64748b' }}>
        Indicators show — when no data exists yet. Record incidents, hand hygiene audits, and patient feedback in Quality / Infection Control modules.
      </div>
    </div>
  );
}

export default function ManagerDashboard() {
  const { user } = useAuth();
  const { data, loading, reload } = useLiveData(() => ipApi.getManagerOverview(), []);
  const { data: dir } = useHospitalDirectory();
  const { data: emrData } = useLiveData(() => ipApi.getEmrSettings(), []);
  const emrSettings = mergeEmrSettings(emrData?.settings);
  const announcements = emrSettings.pushAnnouncements || [];
  const [chartPeriod, setChartPeriod] = useState('week');
  const [dashTab, setDashTab] = useState('overview');

  const hospital = data?.hospital;
  const today = data?.todayStats || {};
  const month = useMemo(() => data?.monthStats || {}, [data]);
  const beds = useMemo(() => data?.bedOccupancy || {}, [data]);
  const quotas = data?.quotas;

  /* Build revenue timeline bars from API data */
  const chartData = useMemo(() => {
    const timeline = data?.revenueTimeline || data?.monthStats?.timeline || [];
    if (timeline.length > 0) {
      return timeline.slice(-7).map((t) => ({ label: (t.date || '').slice(5), value: Number(t.amount || 0) }));
    }
    /* Fallback: generate last 7 days with zeros */
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(); d.setDate(d.getDate() - i);
      days.push({ label: d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }), value: 0 });
    }
    return days;
  }, [data]);

  /* Build smart alerts */
  const alerts = useMemo(() => { // eslint-disable-next-line
    const list = [];
    const bedOcc = beds.total > 0 ? (beds.occupied / beds.total) * 100 : 0;
    if (bedOcc >= 90) list.push({ icon: MdBed, title: 'Bed occupancy critical', desc: `${Math.round(bedOcc)}% beds occupied`, color: '#ef4444', to: '/ipd/beds' });
    if (beds.occupied >= beds.total && beds.total > 0) list.push({ icon: MdWarning, title: 'No beds available!', desc: 'All beds are full', color: '#dc2626', to: '/ipd/beds' });
    if (Number(month.ipOutstanding) > 100000) list.push({ icon: MdAccountBalanceWallet, title: 'High IP outstanding', desc: formatInr(month.ipOutstanding), color: '#f97316', to: '/billing/receipts' });
    if ((data?.queueCount || 0) > 20) list.push({ icon: MdGroups, title: 'Large queue', desc: `${data.queueCount} patients waiting`, color: '#f59e0b', to: '/reception/queue' });
    return list;
  }, [beds, month, data]);

  const queueCols = [
    { key: 'token', label: '#', render: (r) => <span className="token-pill">{r.token || '—'}</span> },
    { key: 'patientName', label: 'Patient' },
    { key: 'doctor', label: 'Doctor' },
    { key: 'status', label: 'Status', render: (r) => <Badge tone="info">{r.status}</Badge> },
    { key: 'time', label: 'Time', render: (r) => (r.time || '').slice(0, 5) },
  ];

  return (
    <PageShell
      title={hospital?.name || user?.hospital || 'Manager Dashboard'}
      subtitle={`${hospital?.city || ''} · ${hospital?.plan || 'Pro'} · Realtime overview`}
      actions={
        <>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => reload()}>
            <MdRefresh /> Refresh
          </button>
          <Link to="/manager/admin" className="btn btn-primary">+ Staff login</Link>
        </>
      }
    >
      {/* ── Tab Navigation ── */}
      <div className="period-tabs card mb">
        <button type="button" className={`emr-chip ${dashTab === 'overview' ? 'active' : ''}`} onClick={() => setDashTab('overview')}>
          🏥 Dashboard Overview
        </button>
        <button type="button" className={`emr-chip ${dashTab === 'analytics' ? 'active' : ''}`} onClick={() => setDashTab('analytics')}>
          📊 Analytics & Reports
        </button>
      </div>

      {dashTab === 'analytics' && <AnalyticsPanel />}

      {dashTab === 'overview' && (<>
      {/* Get Started + announcements */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 16 }}>
        <GetStartedChecklist hospital={hospital} directory={dir} emrSettings={emrSettings} compact />
        {announcements.length > 0 && (
          <div className="card" style={{ borderLeft: '4px solid #f59e0b' }}>
            <h3 style={{ marginTop: 0 }}>Staff updates</h3>
            {announcements.slice(0, 3).map((a) => (
              <div key={a.id} className="hint" style={{ marginBottom: 8, fontSize: 13 }}>
                <strong>{a.by}</strong> · {new Date(a.at).toLocaleDateString('en-IN')}<br />{a.text}
              </div>
            ))}
            <Link to="/hms/clinic-growth" className="hint">Manage →</Link>
          </div>
        )}
      </div>

      {/* ── Alerts bar ── */}
      {alerts.length > 0 && (
        <div className="dash-alerts-row">
          {alerts.map((a, i) => <AlertCard key={i} {...a} />)}
        </div>
      )}

      {/* ── KPI stat cards ── */}
      <div className="stat-grid">
        <StatCard label="OP Revenue Today" value={formatInr(today.opReceiptsTotal)} accent="#10b981" icon={MdTrendingUp} loading={loading} />
        <StatCard label="Month Revenue" value={formatInr(month.opReceiptsTotal)} accent="#059669" icon={MdCalendarMonth} loading={loading} />
        <StatCard label="Queue Now" value={String(data?.queueCount ?? 0)} accent="#4f8ef7" icon={MdGroups} loading={loading} />
        <StatCard label="IP Outstanding" value={formatInr(month.ipOutstanding)} accent="#ef4444" icon={MdAccountBalanceWallet} loading={loading} />
        <StatCard label="Total Patients" value={String(data?.patientCount ?? 0)} accent="#8b5cf6" icon={MdPeople} loading={loading} />
        <StatCard label="Beds" value={`${beds.occupied ?? 0}/${beds.total ?? 0}`} accent="#f97316" icon={MdKingBed} loading={loading} />
        <StatCard label="Staff Active" value={`${data?.staffTotal ?? 0}`} accent="#06b6d4" icon={MdBadge} loading={loading} />
        <StatCard label="New Patients (Month)" value={String(month.newPatients ?? 0)} accent="#6366f1" icon={MdPersonAddAlt1} loading={loading} />
      </div>

      {/* ── Revenue chart + Bed occupancy ── */}
      <div className="dash-two-col">
        <div className="card">
          <div className="card-head-row">
            <h3>Revenue (Last 7 days)</h3>
            <div className="row-actions" style={{ gap: 4 }}>
              {['week', 'month'].map((p) => (
                <button key={p} type="button" className={`emr-chip ${chartPeriod === p ? 'active' : ''}`} onClick={() => setChartPeriod(p)}>
                  {p === 'week' ? 'Week' : 'Month'}
                </button>
              ))}
            </div>
          </div>
          <div className="dash-chart-total">{formatInr(today.opReceiptsTotal)} <span className="hint">today</span></div>
          <MiniBarChart data={chartData} color="#0d6e6a" />
        </div>

        <div className="card">
          <h3>Bed Occupancy</h3>
          <div className="dash-bed-numbers">
            <div className="dash-bed-num" style={{ color: '#ef4444' }}>
              <strong>{beds.occupied ?? 0}</strong>
              <span>Occupied</span>
            </div>
            <div className="dash-bed-num" style={{ color: '#10b981' }}>
              <strong>{(beds.total ?? 0) - (beds.occupied ?? 0)}</strong>
              <span>Available</span>
            </div>
            <div className="dash-bed-num" style={{ color: '#f59e0b' }}>
              <strong>{beds.total ?? 0}</strong>
              <span>Total beds</span>
            </div>
          </div>
          <BedOccupancyBar occupied={beds.occupied} total={beds.total} />
          <Link to="/ipd/beds" className="btn btn-secondary btn-sm mt">View bed map →</Link>
        </div>
      </div>

      {/* ── Department activity ── */}
      <div className="dash-dept-row">
        {[
          { label: 'Pharmacy', icon: MdMedication, color: '#10b981', val: data?.pharmacyPending ?? 0, sub: 'pending orders', to: '/pharmacy/desk' },
          { label: 'Lab', icon: MdScience, color: '#06b6d4', val: data?.labPending ?? 0, sub: 'pending tests', to: '/lab/orders' },
          { label: 'IP Billing', icon: MdReceiptLong, color: '#f97316', val: formatInr(month.ipOutstanding), sub: 'outstanding', to: '/billing/receipts' },
          { label: 'Active IP', icon: MdLocalHospital, color: '#8b5cf6', val: beds.occupied ?? 0, sub: 'inpatients', to: '/ipd/list' },
        ].map((d) => (
          <Link key={d.label} to={d.to} className="dash-dept-card">
            <d.icon style={{ color: d.color, fontSize: 24 }} />
            <div className="dash-dept-val" style={{ color: d.color }}>{d.val}</div>
            <div className="dash-dept-label">{d.label}</div>
            <div className="dash-dept-sub">{d.sub}</div>
          </Link>
        ))}
      </div>

      {/* ── Staff logins by role ── */}
      {quotas && (
        <div className="card mb">
          <div className="card-head-row">
            <h3>Staff by Role</h3>
            <Link to="/manager/admin" className="btn btn-sm btn-primary">Manage all</Link>
          </div>
          <div className="monitor-count-grid">
            {HOSPITAL_STAFF_ROLES.map((r) => (
              <div key={r.id} className="monitor-count-cell">
                <span className="monitor-count-val">{data?.staffByRole?.[r.id] ?? 0}/{quotas.limits?.[r.id] ?? 0}</span>
                <span className="monitor-count-label">{r.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Quick access grid ── */}
      <div className="card mb">
        <h3>Hospital Control Center</h3>
        <p className="hint mb" style={{ marginTop: -4 }}>Tap any module to open instantly</p>
        <div className="manager-quick-grid">
          {QUICK_LINKS.map((q) => {
            const Icon = navIcon(q.path);
            return (
              <Link key={q.path} to={q.path} className="manager-quick-card" style={{ '--qc-accent': q.accent }}>
                <span className="quick-ico" style={{ background: `${q.accent}1a`, color: q.accent }}><Icon /></span>
                <span className="quick-text">
                  <strong>{q.label}</strong>
                  <span className="hint">{q.desc}</span>
                </span>
              </Link>
            );
          })}
        </div>
      </div>

      {/* ── Live queue + Recent patients side by side ── */}
      <div className="form-grid">
        <div className="card">
          <div className="card-head-row">
            <h3>Live OP Queue</h3>
            <Link to="/reception/queue" className="btn btn-sm btn-secondary">Full queue →</Link>
          </div>
          <DataTable columns={queueCols} rows={data?.activeQueue || []} keyField="queueId" loading={loading} skeletonRows={3} emptyMessage="No patients in queue right now." />
        </div>
        <div className="card">
          <div className="card-head-row">
            <h3>Recent Patients</h3>
            <Link to="/reception/patients" className="btn btn-sm btn-secondary">All patients →</Link>
          </div>
          <div className="dash-patient-list">
            {(data?.recentPatients || []).map((p) => (
              <div key={p.patientId} className="dash-patient-row">
                <span className="dash-patient-avatar">{(p.name || 'P').charAt(0)}</span>
                <div>
                  <div className="dash-patient-name">{p.name}</div>
                  <div className="dash-patient-id">{p.patientId} · {p.phone || 'No phone'}</div>
                </div>
              </div>
            ))}
            {!loading && !(data?.recentPatients?.length) && <p className="hint" style={{ padding: '12px 0' }}>No patients yet.</p>}
          </div>
        </div>
      </div>

      <NabhKpiSection beds={beds} />

      {/* ── Recent receipts ── */}
      <div className="card mt">
        <div className="card-head-row">
          <h3>Recent Receipts</h3>
          <Link to="/billing/receipts" className="btn btn-sm btn-secondary">All billing →</Link>
        </div>
        <DataTable
          columns={[
            { key: 'receiptId', label: 'Receipt #' },
            { key: 'patientName', label: 'Patient', render: (r) => r.patientName || r.patient },
            { key: 'service', label: 'Service', render: (r) => r.service || 'Consultation' },
            { key: 'amount', label: 'Amount', render: (r) => formatInr(r.amount || r.total) },
            { key: 'paymentMode', label: 'Mode', render: (r) => r.paymentMode || r.mode || 'Cash' },
            { key: 'createdAt', label: 'Date', render: (r) => (r.createdAt || '').slice(0, 10) },
          ]}
          rows={data?.recentReceipts || []}
          keyField="receiptId"
          emptyMessage="No receipts yet this month."
        />
      </div>
    </>)}
    </PageShell>
  );
}

/* Manager Analytics Panel — live hospital data */
function AnalyticsPanel() {
  const [period, setPeriod] = React.useState('month');
  const apiPeriod = period === 'quarter' ? 'month' : period;
  const { data: stats, loading, error } = useLiveData(() => ipApi.getAnalytics({ period: apiPeriod }), [apiPeriod]);
  const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

  const kpis = stats ? [
    { label: 'OP Collected', val: fmt(stats.opReceiptsTotal), color: '#10b981' },
    { label: 'All Receipts', val: fmt(stats.receiptsTotal), color: '#059669' },
    { label: 'Completed Visits', val: String(stats.completedVisits ?? 0), color: '#6366f1' },
    { label: 'IP Admissions', val: String(stats.ipAdmissions ?? 0), color: '#3b82f6' },
    { label: 'IP Outstanding', val: fmt(stats.ipOutstanding), color: '#f97316' },
    { label: 'Patients', val: String(stats.patientCount ?? 0), color: '#8b5cf6' },
    { label: 'New Patients', val: String(stats.newPatients ?? 0), color: '#06b6d4' },
    { label: 'Avg OP Receipt', val: fmt(stats.avgOpReceipt), color: '#ec4899' },
  ] : [];

  return (
    <div>
      <div className="info-banner mb">
        Live data from billing, queue, and admissions. Export a full PDF from{' '}
        <Link to="/hms/analysis">Analytics &amp; Reports</Link>.
      </div>
      {error && <div className="error mb">{error}</div>}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, flex: 1 }}>Hospital Analytics</h3>
        <div style={{ display: 'flex', gap: 0, borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
          {[['today', 'Today'], ['week', 'Week'], ['month', 'Month'], ['quarter', 'Quarter']].map(([id, label]) => (
            <button key={id} type="button" onClick={() => setPeriod(id)} style={{ padding: '6px 14px', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 12, background: period === id ? '#0d6e6a' : '#f8fafc', color: period === id ? '#fff' : '#475569' }}>{label}</button>
          ))}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 10, marginBottom: 20 }}>
        {(loading && !stats ? [{ label: 'Loading…', val: '…', color: '#94a3b8' }] : kpis).map((s) => (
          <div key={s.label} className="card" style={{ padding: 14, borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 20, fontWeight: 900, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginTop: 3 }}>{s.label}</div>
          </div>
        ))}
      </div>
      {stats?.doctorBreakdown?.length > 0 && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Consultations by doctor</h3>
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>Doctor</th><th>Completed visits</th></tr></thead>
              <tbody>
                {stats.doctorBreakdown.map((d) => (
                  <tr key={d.doctor}><td style={{ fontWeight: 600 }}>{d.doctor}</td><td>{d.count}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {stats?.timeline?.length > 0 && (
        <div className="card mt">
          <h3 style={{ marginTop: 0 }}>OP revenue timeline</h3>
          <ul className="simple-list">
            {stats.timeline.map((t) => (
              <li key={t.date}>{t.date}: {fmt(t.amount)}</li>
            ))}
          </ul>
        </div>
      )}
      {!loading && !stats?.doctorBreakdown?.length && !stats?.timeline?.length && (
        <p className="hint">No activity in this period yet. Book OPD visits and collect receipts to populate analytics.</p>
      )}
    </div>
  );
}
